import type { FC } from 'react';
import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { KanbanColumnConfig } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/hooks/useWorkOrderKanbanColumns';
import type { KanbanColumnsMap } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';
import type { KanbanColumnStatus } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import { WorkOrderKanbanColumn } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/WorkOrderKanbanColumn/WorkOrderKanbanColumn';
import {
  KANBAN_BOARD_HEIGHT,
  WORK_ORDER_UI,
} from '~/app/components/maintenance/work-orders/work-order-ui-colors';

type ActiveDrag =
  | { kind: 'workOrder'; id: string; code: string }
  | { kind: 'ticket'; id: string; code: string };

export interface WorkOrderKanbanBoardProps {
  columns: KanbanColumnConfig[];
  columnsMap: KanbanColumnsMap;
  canManage: boolean;
  organizationId: string;
  selectedWorkOrderId: string | null;
  onSelectWorkOrder: (workOrderId: string) => void;
  onOpenMoveTicket: (workOrderId: string, ticketId: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

export const WorkOrderKanbanBoard: FC<WorkOrderKanbanBoardProps> = ({
  columns,
  columnsMap,
  canManage,
  organizationId,
  selectedWorkOrderId,
  onSelectWorkOrder,
  onOpenMoveTicket,
  onDragEnd,
}) => {
  const { t } = useTranslation('maintenance');
  /** Only used for DragOverlay content — do not fan this out to every card. */
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current;
    if (data?.type === 'ticket') {
      setActiveDrag({
        kind: 'ticket',
        id: String(data.ticketId ?? ''),
        code: String(data.ticketCode ?? ''),
      });
      return;
    }
    if (data?.type === 'workOrder') {
      setActiveDrag({
        kind: 'workOrder',
        id: String(data.workOrderId ?? ''),
        code: String(data.workOrderCode ?? ''),
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null);
    onDragEnd(event);
  };

  const handleDragCancel = () => {
    setActiveDrag(null);
  };

  const overlay = useMemo(() => {
    if (!activeDrag) return null;
    return (
      <div
        style={{
          padding: '10px 14px',
          borderRadius: 10,
          background: '#fff',
          border:
            activeDrag.kind === 'ticket'
              ? `2px solid ${WORK_ORDER_UI.suggestionLink}`
              : `2px solid ${WORK_ORDER_UI.primary}`,
          boxShadow: '0 12px 28px rgba(15, 23, 42, 0.18)',
          minWidth: 160,
        }}
      >
        <Tag
          color={activeDrag.kind === 'ticket' ? 'blue' : 'green'}
          style={{ marginBottom: 6 }}
        >
          {activeDrag.kind === 'ticket'
            ? t('workOrders.kanban.drag.overlayTicket')
            : t('workOrders.kanban.drag.overlayWorkOrder')}
        </Tag>
        <Typography.Text strong>{activeDrag.code}</Typography.Text>
      </div>
    );
  }, [activeDrag, t]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div
        style={{
          display: 'flex',
          gap: 14,
          alignItems: 'stretch',
          overflowX: 'auto',
          overflowY: 'hidden',
          height: KANBAN_BOARD_HEIGHT,
          minHeight: 320,
          paddingBottom: 4,
        }}
      >
        {columns.map((column) => (
          <WorkOrderKanbanColumn
            key={column.status}
            status={column.status as KanbanColumnStatus}
            title={column.title}
            description={column.description}
            workOrders={columnsMap[column.status]}
            canManage={canManage}
            organizationId={organizationId}
            selectedWorkOrderId={selectedWorkOrderId}
            activeDragKind={activeDrag?.kind ?? null}
            activeDragId={activeDrag?.id ?? null}
            onSelectWorkOrder={onSelectWorkOrder}
            onOpenMoveTicket={onOpenMoveTicket}
          />
        ))}
      </div>

      <DragOverlay dropAnimation={null}>{overlay}</DragOverlay>
    </DndContext>
  );
};
